
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatDistanceToNow } from 'date-fns';
import { UserCheck, AlertTriangle } from 'lucide-react';

interface LimboMember {
  id: string;
  full_name: string;
  email: string;
  phone_number: string;
  state: string;
  local_government: string;
  created_at: string;
  approved: boolean;
  has_role: boolean;
}

const LimboMembersSection = () => {
  const [limboMembers, setLimboMembers] = useState<LimboMember[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchLimboMembers = useCallback(async () => {
    setLoading(true);
    try {
      console.log('[LimboMembers] Fetching members in limbo...');
      
      // Get all approved members and check if they have roles
      const { data: membersData, error: membersError } = await supabase
        .from('members')
        .select('id, full_name, email, phone_number, state, local_government, created_at, approved')
        .eq('approved', true)
        .order('created_at', { ascending: true });

      if (membersError) {
        console.error('[LimboMembers] Error fetching members:', membersError);
        throw membersError;
      }

      if (!membersData || membersData.length === 0) {
        setLimboMembers([]);
        return;
      }

      // Get user roles for these members
      const memberIds = membersData.map(m => m.id);
      const { data: rolesData, error: rolesError } = await supabase
        .from('user_roles')
        .select('user_id, approved')
        .in('user_id', memberIds);

      if (rolesError) {
        console.error('[LimboMembers] Error fetching roles:', rolesError);
        throw rolesError;
      }

      // Create a map of members with approved roles
      const approvedRoles = new Set(
        rolesData?.filter(role => role.approved).map(role => role.user_id) || []
      );

      // Filter members who are approved but don't have approved roles
      const limboMembersData = membersData
        .filter(member => !approvedRoles.has(member.id))
        .map(member => ({
          ...member,
          has_role: false
        }));

      console.log('[LimboMembers] Found members in limbo:', limboMembersData.length);
      setLimboMembers(limboMembersData);
    } catch (error) {
      console.error('[LimboMembers] Error:', error);
      toast.error('Failed to load members in limbo.');
      setLimboMembers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLimboMembers();
  }, [fetchLimboMembers]);

  const grantAccess = async (memberId: string) => {
    try {
      console.log('[LimboMembers] Granting access to member:', memberId);

      // First check if the user exists in auth.users
      const { data: authUser, error: authError } = await supabase.auth.admin.getUserById(memberId);
      
      if (authError || !authUser.user) {
        console.error('[LimboMembers] User not found in auth:', authError);
        toast.error('Cannot grant access: User account not found. The member may need to complete their registration first.');
        return;
      }

      // Use the security definer function to safely assign the role
      const { error: roleError } = await supabase.rpc('assign_member_role', {
        member_id: memberId
      });

      if (roleError) {
        console.error('[LimboMembers] Error granting access:', roleError);
        toast.error(`Failed to grant access: ${roleError.message}`);
        return;
      }

      toast.success('Access granted successfully!');
      fetchLimboMembers();
    } catch (error) {
      console.error('[LimboMembers] Unexpected error granting access:', error);
      toast.error('An unexpected error occurred while granting access. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="py-8 text-center">
        <div className="w-8 h-8 border-2 border-omoduduwa-indigo border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
        <p className="text-gray-500">Loading members in limbo...</p>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-gray-900">
          <AlertTriangle className="text-amber-500" />
          Members in Limbo
        </CardTitle>
        <p className="text-gray-700 text-sm">
          Approved members who cannot access the community (missing role permissions)
        </p>
      </CardHeader>
      <CardContent>
        {limboMembers.length === 0 ? (
          <div className="py-8 text-center">
            <UserCheck className="mx-auto h-12 w-12 text-green-300 mb-2" />
            <p className="text-gray-700">No members in limbo - all approved members have proper access!</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-gray-900 font-semibold">Name</TableHead>
                  <TableHead className="text-gray-900 font-semibold">Email</TableHead>
                  <TableHead className="text-gray-900 font-semibold">Phone</TableHead>
                  <TableHead className="text-gray-900 font-semibold">Location</TableHead>
                  <TableHead className="text-gray-900 font-semibold">Approved Date</TableHead>
                  <TableHead className="text-right text-gray-900 font-semibold">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {limboMembers.map((member) => (
                  <TableRow key={member.id}>
                    <TableCell className="font-medium text-gray-900">{member.full_name}</TableCell>
                    <TableCell className="text-gray-700">{member.email}</TableCell>
                    <TableCell className="text-gray-700">{member.phone_number}</TableCell>
                    <TableCell className="text-gray-700">
                      {member.state}, {member.local_government}
                    </TableCell>
                    <TableCell className="text-gray-700">
                      {formatDistanceToNow(new Date(member.created_at), { addSuffix: true })}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700 text-white font-medium"
                        onClick={() => grantAccess(member.id)}
                      >
                        <UserCheck className="w-4 h-4 mr-1" />
                        Grant Access
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default LimboMembersSection;
